import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type UseQueryResult } from "@tanstack/react-query";
import { z } from "zod";

export const EMPLOYMENT_VERIFICATION_STATUS = {
  PENDING: "PENDING",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
} as const;

export type EmploymentVerificationStatus =
  (typeof EMPLOYMENT_VERIFICATION_STATUS)[keyof typeof EMPLOYMENT_VERIFICATION_STATUS];

export const EMPLOYMENT_VERIFICATION_RESOURCE_TYPE = "employment-verification";

const employmentVerificationAttributesSchema = z.object({
  workerId: z.string(),
  workplaceId: z.string(),
  status: z.nativeEnum(EMPLOYMENT_VERIFICATION_STATUS),
  rejectionReason: z.string().optional(),
  reviewedAt: z.string().optional(),
  createdAt: z.string(),
  isExpired: z.boolean(),
});

const employmentVerificationSchema = z.object({
  id: z.string(),
  type: z.literal(EMPLOYMENT_VERIFICATION_RESOURCE_TYPE),
  attributes: employmentVerificationAttributesSchema,
});

const getEmploymentVerificationsResponseSchema = z.object({
  data: z.array(employmentVerificationSchema),
  links: z
    .object({
      prev: z.string().optional(),
      next: z.string().optional(),
    })
    .optional(),
});

export type GetEmploymentVerificationsResponse = z.infer<
  typeof getEmploymentVerificationsResponseSchema
>;

const getEmploymentVerificationsQuerySchema = z.object({
  filter: z
    .object({
      workerId: z.string().optional(),
      workplaceId: z.string().optional(),
    })
    .optional(),
  page: z
    .object({
      size: z.coerce.number(),
    })
    .optional(),
});

type GetEmploymentVerificationsQuery = z.infer<typeof getEmploymentVerificationsQuerySchema>;

export const GET_EMPLOYMENT_VERIFICATIONS_PATH = "/employment-verifications";

export function useGetEmploymentVerifications(
  params: GetEmploymentVerificationsQuery,
  options: UseGetQueryOptions<GetEmploymentVerificationsResponse> = {}
): UseQueryResult<GetEmploymentVerificationsResponse> {
  return useGetQuery({
    url: `${environmentConfig.REACT_APP_SHIFT_REVIEW_API_URL}${GET_EMPLOYMENT_VERIFICATIONS_PATH}`,
    queryParams: params,
    requestSchema: getEmploymentVerificationsQuerySchema,
    responseSchema: getEmploymentVerificationsResponseSchema,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_EMPLOYMENT_VERIFICATIONS_FAILURE,
      userErrorMessage: "Something went wrong while loading employment verifications",
    },
    ...options,
  });
}
